import { defineStore } from 'pinia'

import type { Account } from '@/stores/interfaces/Account'
import { getSubscriptionItems, getAccount } from '@/api'
import {
  SUBSCRIPTION_TYPES,
  type SubsriptionType,
  type PlanType,
  PLAN_TYPES,
} from '@/api/interfaces'
import { includes } from '@/helpers/utils'

export const useAccountStore = defineStore('account', {
  state: (): Account => ({
    account: {
      accountId: '',
      accountType: '',
      companyName: '',
      website: '',
      vatId: '',
      profileDescription: '',
      imageURL: '',
      discounts: [],
      signupId: '',
      signupComplete: false,
      emailAlreadyUsed: false,
      birthday: {
        year: null,
        month: null,
        day: null,
      },
      phoneNumber: '',
      marketingAccepted: false,
      termsAndConditions: false,
      address: {
        street: '',
        postCode: '',
        city: '',
        country: '',
      },
      personalPlans: {
        personal: null,
        plasticHero: null,
        earthHero: null,
        positiveFamilyAdult: null,
        positiveFamilyChild: null,
        positiveFamilyPet: null,
        earthPositiveFamilyAdult: null,
        earthPositiveFamilyChild: null,
        earthPositiveFamilyPet: null,
      },
      widgets: [],
      widgetFolderName: '',
      livePageId: '',
      signupDate: '',
      currency: 'dollar',
      currencyPreChange: 'dollar',
      userFromMarketplace: false,
      sustainabilitySite: '',
      createdAt: '',
    },
    subscriptions: [],
  }),
  getters: {
    getActiveSubscription(state: Account) {
      return state.subscriptions.find((subscriptionItem) => {
        return (
          includes(SUBSCRIPTION_TYPES, subscriptionItem.product) &&
          new Date() >= new Date(subscriptionItem.startDate) &&
          (!subscriptionItem.endDate || new Date() <= new Date(subscriptionItem.endDate))
        )
      })
    },
    getPlanName() {
      return (planType: SubsriptionType | undefined): PlanType => {
        switch (planType) {
          case 'free':
          case 'zero':
          case 'freeBusiness':
          case 'starterBusiness':
          case 'starterBusinessYearly':
            return PLAN_TYPES.STARTER
          case 'ECOMMERCE':
          case 'ecommerce':
          case 'businessLegacy':
          case 'growthBusiness':
          case 'growthBusinessYearly':
            return PLAN_TYPES.GROWTH
          case 'premiumBusiness':
          case 'premiumBusinessYearly':
          case 'enterpriseBusiness':
            return PLAN_TYPES.PREMIUM
          default:
            throw new Error('Unknown plan type')
        }
      }
    },
    getAcitvePlanName(): PlanType {
      const product = this.getActiveSubscription?.product
      return this.getPlanName(product)
    },
    hasStarterPlan(): boolean {
      return this.getAcitvePlanName === PLAN_TYPES.STARTER
    },
    hasGrowthPlan(): boolean {
      return this.getAcitvePlanName === PLAN_TYPES.GROWTH
    },
    hasPremiumPlan(): boolean {
      return this.getAcitvePlanName === PLAN_TYPES.PREMIUM
    },
  },
  actions: {
    async fetchSubscriptions() {
      try {
        const { data } = await getSubscriptionItems()
        this.$patch({ subscriptions: [...data] })
      } catch (e) {
        console.error(e)
      }
    },
    async fetchAccount() {
      try {
        const { data } = await getAccount()
        this.$patch({ account: { ...data } })
      } catch (e) {
        console.error(e)
      }
    },
  },
})
